import { sectionRenderer } from '@theme/section-renderer';

/**
 * Loads the next page of blog posts via the Section Rendering API
 * and appends the new cards. Mirrors collection pagination instead of
 * toggling pre-rendered cards with display none/block.
 */
class BlogPostsGrid extends HTMLElement {
  /** @type {boolean} */
  #loading = false;

  /** @type {((event: Event) => void) | null} */
  #onLoadMore = null;

  connectedCallback() {
    const button = this.#button;
    if (!button) return;

    this.#onLoadMore = this.#handleLoadMore.bind(this);
    button.addEventListener('click', this.#onLoadMore);
  }

  disconnectedCallback() {
    if (this.#button && this.#onLoadMore) {
      this.#button.removeEventListener('click', this.#onLoadMore);
    }
    this.#onLoadMore = null;
  }

  /**
   * @param {Event} event
   */
  async #handleLoadMore(event) {
    event.preventDefault();

    const button = this.#button;
    const grid = this.#grid;
    if (!button || !grid || this.#loading) return;

    const nextPage = Number(button.dataset.nextPage);
    const lastPage = Number(grid.dataset.lastPage);
    if (!nextPage || nextPage > lastPage) {
      button.hidden = true;
      return;
    }

    this.#loading = true;
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('disabled', '');

    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set('page', String(nextPage));
    pageUrl.hash = '';

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, new URL(pageUrl));
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incomingGrid = parsed.querySelector('[data-blog-posts-grid]');
      const incomingCards = incomingGrid?.querySelectorAll('[data-blog-posts-card]');

      if (incomingCards?.length) {
        incomingCards.forEach((card) => {
          grid.appendChild(document.importNode(card, true));
        });
      }

      history.pushState('', '', pageUrl.toString());

      if (nextPage >= lastPage) {
        button.hidden = true;
        return;
      }

      const upcomingPage = nextPage + 1;
      button.dataset.nextPage = String(upcomingPage);
      if (button instanceof HTMLAnchorElement) {
        const nextUrl = new URL(pageUrl);
        nextUrl.searchParams.set('page', String(upcomingPage));
        button.href = nextUrl.pathname + nextUrl.search;
      }
    } catch (error) {
      console.error('Blog posts: failed to load the next page', error);
    } finally {
      this.#loading = false;
      button.removeAttribute('aria-busy');
      button.removeAttribute('disabled');
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  get #grid() {
    return this.querySelector('[data-blog-posts-grid]');
  }

  get #button() {
    return this.querySelector('[data-blog-posts-load-more]');
  }
}

if (!customElements.get('blog-posts-grid')) {
  customElements.define('blog-posts-grid', BlogPostsGrid);
}
